import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { saveContactMessage, isValidEmail } from "@/lib/contact";
import { CONTACT_EMAIL, STUDIO_LOCATION } from "@/constants/siteData";

const initialForm = { name: "", email: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [sent, setSent] = useState(false);

  const handleChange = (field) => (e) => {
    setSent(false);
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name required.");
      return;
    }
    if (!isValidEmail(form.email)) {
      toast.error("Valid email required.");
      return;
    }
    if (!form.message.trim()) {
      toast.error("Message required.");
      return;
    }
    saveContactMessage(form);
    setSent(true);
    toast.success("Message transmitted.", {
      description: "We'll respond directly.",
    });
    setForm(initialForm);
  };

  return (
    <section id="contact" className="relative border-t-2 border-border py-24 sm:py-32">
      <div className="container-xl grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <span className="eyebrow-mono">// Initiate Dialogue</span>
          <h2 className="mt-4 font-display text-3xl font-semibold text-foreground sm:text-4xl lg:text-5xl">
            Contact.
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
            For partnership, acquisition and investment inquiries.
          </p>

          <div className="mt-10 divide-y-2 divide-border border-y-2 border-border">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="flex items-center justify-between gap-4 py-4 hover:opacity-70 transition-opacity"
            >
              <span className="font-mono-brand text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Email
              </span>
              <span className="text-base font-medium text-foreground">{CONTACT_EMAIL}</span>
            </a>
            <div className="flex items-center justify-between gap-4 py-4">
              <span className="font-mono-brand text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Location
              </span>
              <span className="text-base font-medium uppercase text-foreground">
                {STUDIO_LOCATION}
              </span>
            </div>
          </div>

        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.08 }}
          onSubmit={handleSubmit}
          className="flex flex-col gap-7"
        >
          <div className="space-y-2">
            <label className="font-mono-brand text-xs uppercase tracking-[0.16em] text-muted-foreground">
              &gt; Name
            </label>
            <Input
              value={form.name}
              onChange={handleChange("name")}
              placeholder="Your name"
              className="h-11 rounded-none border-0 border-b-2 border-input bg-transparent px-0 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-foreground"
            />
          </div>
          <div className="space-y-2">
            <label className="font-mono-brand text-xs uppercase tracking-[0.16em] text-muted-foreground">
              &gt; Email
            </label>
            <Input
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              placeholder="you@studio.com"
              className="h-11 rounded-none border-0 border-b-2 border-input bg-transparent px-0 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-foreground"
            />
          </div>
          <div className="space-y-2">
            <label className="font-mono-brand text-xs uppercase tracking-[0.16em] text-muted-foreground">
              &gt; Message
            </label>
            <Textarea
              value={form.message}
              onChange={handleChange("message")}
              placeholder="Tell us about your proposal"
              rows={4}
              className="rounded-none border-0 border-b-2 border-input bg-transparent px-0 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-foreground resize-none"
            />
          </div>
          <Button type="submit" variant="brutal" className="mt-2 h-12 w-full sm:w-auto px-8">
            {sent ? "Transmitted" : "Transmit"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </motion.form>
      </div>
    </section>
  );
};
